import { DeviceContext } from '@/hooks/common/DeviceContext';
import React, { use } from 'react';

interface ReviewStatusCardsProps {
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
  approvedDuration?: number;
  pendingDuration?: number;
  rejectedDuration?: number;
  onStatusClick?: (status: string) => void;
  activeStatus?: string;
  showDuration?: boolean; // New prop to control duration display
}

/**
 * ReviewStatusCards - Status overview cards for content review
 * 
 * Displays interactive status cards showing counts for approved, pending, and rejected content.
 * Features gradient borders, hover effects, and active state indication.
 * 
 * Features:
 * - Interactive status filtering
 * - Gradient border styling
 * - Active state indication
 * - Hover effects
 * - Responsive design
 * - Decorative separator line
 * 
 * @param approvedCount - Number of approved items
 * @param pendingCount - Number of pending items  
 * @param rejectedCount - Number of rejected items
 * @param onStatusClick - Optional callback when status card is clicked
 * @param activeStatus - Currently active status filter
 */
const ReviewStatusCards: React.FC<ReviewStatusCardsProps> = ({
  approvedCount,
  pendingCount,
  rejectedCount,
  approvedDuration = 0,
  pendingDuration = 0,
  rejectedDuration = 0,
  onStatusClick,
  activeStatus,
  showDuration = true // Default to true for backward compatibility
}) => {

  const { isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };

  /**
   * Format duration in minutes to a readable string
   */
  const formatDuration = (minutes: number): string => {
    if (minutes < 1) return '< 1 min';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    if (remainingMinutes === 0) return `${hours}h`;
    return `${hours}h ${remainingMinutes}m`;
  };

  /**
   * Configuration for status cards with styling and data
   */
  const cards = [
    {
      status: 'approved',
      label: 'Approved',
      count: approvedCount,
      duration: approvedDuration,
      bgColor: 'bg-white',
      hoverColor: 'hover:bg-green-50',
      textColor: 'text-green-600',
      description: 'Content ready for publication'
    },
    {
      status: 'pending',
      label: isMobile ? 'Pending' : 'Pending Review',
      count: pendingCount,
      duration: pendingDuration,
      bgColor: 'bg-white',
      hoverColor: 'hover:bg-yellow-50',
      textColor: 'text-yellow-600',
      description: 'Awaiting review and approval'
    },
    {
      status: 'rejected',
      label: 'Rejected',
      count: rejectedCount,
      duration: rejectedDuration,
      bgColor: 'bg-white',
      hoverColor: 'hover:bg-red-50',
      textColor: 'text-red-600',
      description: 'Content requires revision'
    }
  ];

  /**
   * Handles status card click with haptic feedback simulation
   */
  const handleCardClick = (status: string) => {
    onStatusClick?.(status);
    
    // Provide visual feedback
    const card = document.getElementById(`status-card-${status}`);
    if (card) {
      card.style.transform = 'scale(0.98)';
      setTimeout(() => {
        card.style.transform = 'scale(1)';
      }, 100);
    }
  };

  /**
   * Renders an individual status card
   */
  const renderStatusCard = (card: typeof cards[0]) => {
    const isActive = activeStatus === card.status;
    const isClickable = Boolean(onStatusClick);
    
    return (
      <div
        key={card.status}
        id={`status-card-${card.status}`}
        className="relative group"
        style={{
          width: isMobile ? '110px' : '227px',
          height: '82px',
          borderRadius: '13px',
          padding: '1px',
          background: 'linear-gradient(to right, #F39A81, #959BEA)',
        }}
      >
        <button
          onClick={() => handleCardClick(card.status)}
          disabled={!isClickable}
          className={`
            w-full h-full transition-all duration-200 cursor-pointer text-center flex flex-col justify-center relative overflow-hidden
            ${card.bgColor} ${card.hoverColor}
            ${isActive ? 'ring-2 ring-blue-500 ring-opacity-50 ring-offset-1 shadow-lg' : 'shadow-sm hover:shadow-md'}
            ${isClickable ? '' : 'cursor-default'}
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
          `}
          style={{
            borderRadius: '12px',
            border: '1px solid transparent',
            transform: 'translateZ(0)', // Enable hardware acceleration
            outline: 'none', // Remove default browser outline
          }}
          title={card.description}
        >
          {/* Active indicator */}
          {isActive && (
            <div className="absolute top-2 right-2 w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          )}
          
          {/* Card content */}
          <div className="relative z-10">
            <div className={`text-sm font-medium mb-1 transition-colors ${isActive ? card.textColor : 'text-gray-700'}`}>
              {card.label}
            </div>
            <div className={`text-2xl font-bold transition-colors ${isActive ? card.textColor : 'text-gray-900'}`}>
              {card.count.toLocaleString()}
            </div>
            {showDuration && (
              <div className={`text-xs font-medium mt-1 transition-colors ${isActive ? card.textColor : 'text-gray-600'}`}>
                {formatDuration(card.duration)}
              </div>
            )}
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-300 pointer-events-none"></div>
        </button>
      </div>
    );
  };


  return (
    <div className="flex flex-col items-center mb-8">
      {/* Status Cards Grid */}
      <fieldset className={`flex flex-wrap justify-center ${isMobile ? 'gap-[10px]' : 'gap-12'} mb-10`} aria-label="Content status overview">
        {cards.map(renderStatusCard)}
      </fieldset>

      {/* Decorative Separator Line */}
      <div className="flex items-center justify-center w-full max-w-md" aria-hidden="true">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#F39A81] to-[#F39A81] opacity-60"></div>
        
        {/* Central ornament */}
        <div
          className="mx-4 sm:mx-6 w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 border-transparent animate-pulse"
          style={{
            background:
              "linear-gradient(white, white) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
          }}
        ></div>
        
        <div className="flex-1 h-px bg-gradient-to-l from-transparent via-[#959BEA] to-[#959BEA] opacity-60"></div>
      </div>      
    </div>
  );
};

export default ReviewStatusCards;
